import type { AssignmentFormData } from '../../types/assignmentTypes';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useValidateAssignment = () => {
  const { t } = useTranslation();
  const {
    activeForm,
    stepIndex,
    setStepIndex,
    audienceCount,
    setAudience500ModalIsOpen,
    setAudienceExceedsLimitModalIsOpen,
  } = useAdminAssignmentsStore();
  const { watch, setError } = useFormContext<AssignmentFormData>();
  const { setToast } = useToastStore();
  const { config } = useConfigStore();
  const { assignmentGroupSizeLimit } = config;
  const location = useLocation();
  const { pathname } = location;
  const isInstance = pathname.includes('instance');
  const isEdit = pathname.includes('edit');
  const isInstanceEdit = isInstance && isEdit;

  const {
    audience,
    settings,
    formBoundType,
    simpleType,
    useCustomDueDate,
    setDueDate,
    audienceIsAll,
  } = watch();

  const { name, triggerObject, responses, daysToComplete, type } = settings;

  const setErrorToast = () =>
    setToast({
      show: true,
      status: 'error',
      title: t('admin.assignments.automations.errorsOnPage'),
    });

  const validateGeneralDetailsForm = () => {
    if (!name || !simpleType) {
      if (!name) {
        setError('settings.name', {
          type: 'required',
        });
      }

      if (!simpleType) {
        setError('simpleType', {
          type: 'required',
        });
      }

      setErrorToast();

      return;
    }

    setStepIndex(stepIndex + 1);
  };

  const validateAutomationsForm = () => {
    if (!type || type === WorkflowType.PointInTime) {
      setError('settings.type', {
        type: 'required',
      });

      setErrorToast();

      return;
    }

    const hasTriggerObject = triggerObject?.id && triggerObject?.title;

    if (
      (type === WorkflowType.CompletedContent ||
        type === WorkflowType.CompletedAssignment ||
        type === WorkflowType.AttendedWorkshop ||
        type === WorkflowType.RegisteredWorkshop) &&
      !hasTriggerObject
    ) {
      setError('settings.triggerObject', {
        type: 'required',
      });

      setErrorToast();

      return;
    }

    setStepIndex(stepIndex + 1);
  };

  // const validateOnWorkflowType = () => {
  //   if (!type || type === WorkflowType.PointInTime) {
  //     setError('settings.type', {
  //       type: 'required',
  //     });

  //     setErrorToast();

  //     return;
  //   }

  //   setStepIndex(stepIndex + 1);
  // };

  // const validateUserAddedUpdatedForm = () => {
  //   if (
  //     !formBoundType ||
  //     (formBoundType !== WorkflowType.UserAdded &&
  //       formBoundType !== WorkflowType.UserUpdated &&
  //       formBoundType !== WorkflowType.UserAddedOrUpdated)
  //   ) {
  //     setError('formBoundType', {
  //       type: 'required',
  //     });

  //     setErrorToast();

  //     return;
  //   }

  //   setStepIndex(stepIndex + 1);
  // };

  // const validateOnTriggerObject = () => {
  //   if (!triggerObject?.id || !triggerObject?.title) {
  //     setError('settings.triggerObject', {
  //       type: 'required',
  //     });

  //     setErrorToast();

  //     return;
  //   }

  //   setStepIndex(stepIndex + 1);
  // };

  // const validateOnTriggerObjectWithFormBoundType = () => {
  //   if (!triggerObject?.id || !triggerObject?.title) {
  //     setError('settings.triggerObject', {
  //       type: 'required',
  //     });

  //     setErrorToast();

  //     return;
  //   }

  //   if (!formBoundType) {
  //     setError('formBoundType', {
  //       type: 'required',
  //     });

  //     return;
  //   }

  //   setStepIndex(stepIndex + 1);
  // };

  // const validateWorkshopForm = () => {
  //   if (type !== WorkflowType.AttendedWorkshop && type !== WorkflowType.RegisteredWorkshop) {
  //     setError('formBoundType', {
  //       type: 'required',
  //     });

  //     setErrorToast();

  //     return;
  //   }

  //   if (!triggerObject?.id || !triggerObject?.title) {
  //     setError('settings.triggerObject', {
  //       type: 'required',
  //     });

  //     return;
  //   }

  //   setStepIndex(stepIndex + 1);
  // };

  const validateAudienceForm = () => {
    if (type === WorkflowType.PointInTime && !audienceIsAll && audience.length === 0) {
      setError('audience', {
        type: 'required',
      });

      setErrorToast();

      return;
    }

    if (audienceCount > assignmentGroupSizeLimit && !isInstanceEdit) {
      setAudienceExceedsLimitModalIsOpen(true);
      return;
    }

    if (audienceCount > 500 && !isInstanceEdit) {
      setAudience500ModalIsOpen(true);
      return;
    }

    setStepIndex(stepIndex + 1);
  };

  const validateContentForm = () => {
    if (!responses.length) {
      setError('settings.responses', {
        type: 'required',
      });

      setErrorToast();

      return;
    }

    setStepIndex(stepIndex + 1);
  };

  const validateSettingsForm = () => {
    const customDueDateInvalid = useCustomDueDate && !daysToComplete;

    const presetDueDateInvalid = !useCustomDueDate && !daysToComplete;

    if (setDueDate) {
      if (customDueDateInvalid) {
        setError('customDueDate', {
          type: 'required',
        });

        setErrorToast();

        return;
      }

      if (presetDueDateInvalid) {
        setError('settings.daysToComplete', {
          type: 'required',
        });

        setErrorToast();

        return;
      }
    }

    setStepIndex(stepIndex + 1);
  };

  const validationActionMap = {
    generalDetails: () => validateGeneralDetailsForm(),
    automations: () => validateAutomationsForm(),
    // userAddedUpdated: () => validateUserAddedUpdatedForm(),
    // completedContent: () => validateOnTriggerObject(),
    // completedAssignment: () => validateOnTriggerObject(),
    // attendedRegisteredWorkshop: () => validateWorkshopForm(),
    // hiredDaysAgo: () => validateOnWorkflowType(),
    audience: () => validateAudienceForm(),
    content: () => validateContentForm(),
    settings: () => validateSettingsForm(),
    notifications: () => setStepIndex(stepIndex + 1),
  };

  const validateCurrentForm = () => {
    validationActionMap[activeForm as keyof typeof validationActionMap]();
  };

  return { validateCurrentForm };
};
